// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { TippyPopupStyle } from "@hardwoods/App/Components/Tippy/Interface";
import TippyPopup from "@hardwoods/App/Components/Tippy/TippyPopup";
import XngSignIn, { SignInComponentStyles } from "@hardwoods/App/Components/XngSignIn/XngSignIn";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon, getHeader } from "@hardwoods/App/Store/Selectors/CommonState";
import HeaderPopupLink from "@hardwoods/Overrides/Widgets/Common/HeaderPopupLink";
import Account from "@hardwoods/XDL/Icons/Account";
import SignOut from "@hardwoods/XDL/Icons/SignOut";
import { capitalize, color } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { styledScrollbar, xGap } from "@hardwoods/XDL/Utilities/SharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius//Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import TypographyEllipsis from "@insite/mobius/Typography/TypographyEllipsis";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    links = "links",
    isGroup = "isGroup",
}

export interface CustomLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        title: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.links]: CustomLinkModel[];
    };
}

const getAllowedLinks = (state: ApplicationState, ownProps: OwnProps) => {
    return (ownProps.fields.links || [])
        .map((linkItem: CustomLinkModel) => {
            const node = getPageLinkByNodeId(state, linkItem?.fields?.destination?.value);
            return {
                isGroup: linkItem.fields.isGroup,
                url: node?.url || "",
                excludeFromNavigation: node?.excludeFromNavigation,
                title: linkItem.fields.isGroup ? linkItem.fields.title : node?.title,
                pageType: node?.type,
            };
        })
        .filter(i => !i.excludeFromNavigation || i.isGroup);
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const session = getSession(state);
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        firstName: session.firstName || session.userName,
        lastName: session.lastName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,

        // XNG-Changes
        isSticky: getHeader(state).isSticky,
        session: getSession(state),
        selectedBillToId: session.billToId,
        billTo: getBillToState(state, session.billToId),
        shipTo: getShipToState(state, cart ? cart.shipToId : undefined),
        links: getAllowedLinks(state, ownProps),
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        isMobile: getCommon(state).isMobile,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setIsReorderConfirmationModalOpen,
    setNextAction,

    // XNG-Changes: customization
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    firstLetter?: ClickablePresentationProps;
    titleClickable?: ClickablePresentationProps;
    userIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: LinkPresentationProps;

    // XNG-Changes
    signInWrapper?: InjectableCss;
    myAccountContainer: InjectableCss;
    myAccountHeader: InjectableCss;
    myAccountHeaderLink?: LinkPresentationProps;
    myAccountBody: InjectableCss;
    myAccountGroup: InjectableCss;
    myAccountLinkWrapper: InjectableCss;
    myAccountLink: LinkPresentationProps;
    myAccountFooter: InjectableCss;
    changeCustomerClickable?: ButtonPresentationProps;
    information: InjectableCss;
    customerInformationWrapper: InjectableCss;
    tippyPopup?: TippyPopupStyle;
    cartIcon?: InjectableCss;
    xngSignIn?: SignInComponentStyles;
    salesRepLink: LinkPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    myAccountContainer: {
        css: css`
            width: 100%;
            min-width: 300px;
            min-height: 200px;

            ${xScreen.lte("sm")} {
                max-width: 100%;
                height: 100vh;
                display: flex;
                flex-direction: column;
                min-width: initial;
                padding-bottom: 40px;
            }

            ${styledScrollbar(4)}
            overflow-y: auto;
        `,
    },
    myAccountHeader: {
        css: css`
            font-size: 16px;
            letter-spacing: 0;
            line-height: 1;
            padding: 20px;

            ${xScreen.lte("sm")} {
                position: relative;
            }
        `,
    },
    myAccountHeaderLink: {
        color: "text.main",
        typographyProps: {
            size: 16,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
    },
    myAccountBody: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 0 20px 20px;
            ${xGap("0 20px")}
        `,
    },
    myAccountGroup: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 15px;
            width: 100%;
        `,
    },
    myAccountLinkWrapper: {
        css: css`
            width: calc(50% - 20px);
            &&& {
                margin-bottom: 10px;
            }
        `,
    },
    myAccountLink: {
        typographyProps: { size: 12, lineHeight: "18px" },
        css: css`
            &:focus {
                outline: 0;
            }
        `,
    },
    myAccountFooter: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            padding: 30px 15px 25px;

            ${xScreen.lte("sm")} {
                margin-top: 20px;
            }
        `,
    },
    signOutClickable: {
        color: "secondary",
        typographyProps: { size: 12, lineHeight: "14px", weight: "bold" },
        icon: {
            iconProps: {
                src: SignOut,
                color: "secondary",
                size: 11,
                css: css`
                    opacity: 0.33;
                `,
            },
        },
    },
    changeCustomerClickable: {
        sizeVariant: "xSmall" as any,
        variant: "primary",
        css: css`
            &&& {
                min-width: 25%;
            }
        `,
    },
    customerInformationWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xGap("10px")}
            padding: 0 20px;
            ${xScreen.lte("xs")} {
                flex-wrap: wrap;
                margin-bottom: 15px;
            }
        `,
    },
    information: {
        css: css`
            width: calc(50% - 10px);
            min-width: 185px;
            color: ${color("text.main")};
            font-size: 12px;
            letter-spacing: 0;
            line-height: 18px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;

            ${xScreen.lte("xs")} {
                max-width: 100%;
                padding-right: 0;
                margin-bottom: 20px;
            }
        `,
    },
    xngSignIn: {
        form: {
            css: css`
                padding: 20px 20px 25px;
            `,
        },
        titleWrapper: {
            css: css`
                margin-bottom: 20px;
            `,
        },
    },
    salesRepLink: {
        css: css`
            margin-top: 10px;
        `,
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    firstName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    links,
    session,
    selectedBillToId,
    billTo,
    shipTo,
    changeCustomerUrl,
    isMobile,
    loadBillTo,
    displayChangeCustomerLink,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
}: Props) => {
    const [isPopupVisible, setPopupVisibility] = useState(false);
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";
    const primarySalesperson = JSON.parse(session.properties["primarySalesperson"] || "{}");

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };
    if (fields.icon === "User") {
        icon = User;
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if (isMobile) {
            return;
        }

        if ((!firstName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        hideTippy();
    };

    const onPopupVisibilityUpdate = (popupVisibility: boolean) => {
        setPopupVisibility(popupVisibility);
    };

    const SignInElement = () => (
        <HeaderPopupLink
            showIcon={showIcon}
            showLabel={showLabel}
            icon={Account}
            isOpened={isPopupVisible}
            onClick={onSignInHandler}
            iconProps={{ type: "signIn" }}
            clickableProps={{
                "data-test-selector": "header_signIn",
            }}
        >
            {translate("Sign In / Register")}
        </HeaderPopupLink>
    );

    const UserElement = () => (
        <HeaderPopupLink
            showIcon={showIcon}
            showLabel={showLabel}
            icon={Account}
            isOpened={isPopupVisible}
            onClick={() => !isMobile && history.push(myAccountPageLink?.url as string)}
            iconProps={{ type: "signIn" }}
            clickableProps={{
                "data-test-selector": "header_signIn",
            }}
            titleProps={{
                "data-test-selector": "header_userName",
            }}
        >
            <TypographyEllipsis>{`${session.firstName} ${session.lastName}`}</TypographyEllipsis>
            <TypographyEllipsis className="sub-title">{billTo?.value?.companyName}</TypographyEllipsis>
        </HeaderPopupLink>
    );

    const MyAccountOverlay = () => (
        <StyledWrapper {...styles.myAccountContainer}>
            <StyledWrapper {...styles.myAccountHeader}>
                <Link
                    href={myAccountPageLink?.url}
                    onClick={() => {
                        hideTippy();
                    }}
                    {...styles.myAccountHeaderLink}
                >
                    <Typography weight="bold">
                        {translate("My Account")}&nbsp;&nbsp;{billTo?.value?.companyName}
                    </Typography>
                </Link>
            </StyledWrapper>
            <StyledWrapper {...styles.myAccountBody} data-test-selector="header_accountMenu">
                {links.map((link: any) =>
                    link?.isGroup ? (
                        <Typography as="div" key={uniqueId()} {...styles.myAccountGroup}>
                            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                            {translate(link.title)}
                        </Typography>
                    ) : (
                        <StyledWrapper {...styles.myAccountLinkWrapper} key={uniqueId()}>
                            <Link
                                href={link.url}
                                {...styles.myAccountLink}
                                onClick={() => {
                                    hideTippy();
                                }}
                            >
                                {link.title}
                            </Link>
                        </StyledWrapper>
                    ),
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.customerInformationWrapper}>
                <StyledWrapper {...styles.information}>
                    <StyledWrapper {...styles.myAccountGroup}>{translate("Shipping Address")} </StyledWrapper>
                    <AddressInfoDisplay
                        // firstName={shipTo?.value?.firstName}
                        // lastName={shipTo?.value?.lastName}
                        // companyName={shipTo?.value?.companyName}
                        // attention={shipTo?.value?.attention}
                        address1={shipTo?.value?.address1 || ""}
                        // address2={shipTo?.value?.address2 || ""}
                        // address3={shipTo?.value?.address3 || ""}
                        // address4={shipTo?.value?.address4 || ""}
                        city={shipTo?.value?.city || ""}
                        state={shipTo?.value?.state ? shipTo?.value?.state.abbreviation : undefined}
                        postalCode={shipTo?.value?.postalCode || ""}
                        // country={shipTo?.value?.country ? shipTo?.value?.country.abbreviation : undefined}
                        // phone={shipTo?.value?.phone}
                        // fax={shipTo?.value?.fax}
                        // email={shipTo?.value?.email}
                        extendedStyles={{
                            wrapper: {
                                css: css`
                                    p {
                                        font-style: normal;
                                        font-size: 12px;
                                        line-height: 18px;
                                    }
                                    margin-bottom: 20px;
                                `,
                            },
                        }}
                    />
                    {session.displayChangeCustomerLink && (
                        <Button
                            {...styles.changeCustomerClickable}
                            onClick={() => {
                                history.push(changeCustomerUrl as string);
                                hideTippy();
                            }}
                        >
                            {translate("Change Ship-To")}
                        </Button>
                    )}
                </StyledWrapper>
                {primarySalesperson && primarySalesperson.email && (
                    <StyledWrapper {...styles.information}>
                        <StyledWrapper {...styles.myAccountGroup}>{translate("Sales Rep")}</StyledWrapper>
                        {primarySalesperson?.name && <Typography>{primarySalesperson?.name}</Typography>}
                        {primarySalesperson.email && (
                            <Link
                                color="primary"
                                href={`mailto:${primarySalesperson.email}`}
                                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                    e.defaultPrevented = true;
                                }}
                                {...styles.salesRepLink}
                            >
                                {primarySalesperson.email}
                            </Link>
                        )}
                        {primarySalesperson.phone1 && (
                            <Link
                                color="common.ui14"
                                href={`tel:${primarySalesperson.phone1}`}
                                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                    e.defaultPrevented = true;
                                }}
                                {...styles.salesRepLink}
                            >
                                {primarySalesperson.phone1}
                            </Link>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.myAccountFooter}>
                <Link {...styles.signOutClickable} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );

    const tippyOptions: { [key: string]: any } = {
        offset: [0, 0],
        placement: "bottom-end",
        maxWidth: !fields.includeAccountMenu || !firstName || (firstName && currentUserIsGuest) ? 302 : 425,
        onPopupVisibilityUpdate,
        arrow: false,
        withCloseButtonOnMobile: true,
    };

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {fields.includeAccountMenu && firstName && !currentUserIsGuest && (
                <>
                    <TippyPopup
                        updateTriggerOptionsOnMobile
                        enableMobileMode
                        content={<MyAccountOverlay />}
                        className="no-padding"
                        extendedStyles={styles.tippyPopup}
                        {...tippyOptions}
                    >
                        {UserElement()}
                    </TippyPopup>
                </>
            )}
            {(!fields.includeAccountMenu || !firstName || (firstName && currentUserIsGuest)) && (
                <>
                    <TippyPopup
                        updateTriggerOptionsOnMobile
                        enableMobileMode
                        className="no-padding"
                        content={<XngSignIn extendedStyles={styles.xngSignIn} theme={theme} />}
                        {...tippyOptions}
                    >
                        {SignInElement()}
                    </TippyPopup>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In / MyAccount Links",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    if (item.fields[fields.isGroup]) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: fields.isGroup,
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "title",
                        displayName: "Group Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[fields.isGroup] === true,
                    },
                ],
            },
        ],
        group: "Common",
    },
};
export default widgetModule;
